<script setup>
import { HDropdown, HBadge, HRow, HButton, HCol, HDivider } from '@pixul/hephaestus';
import NavbarItem                                           from '@/components/Navbar/NavbarItem.vue';
import NotificationLine                                     from '@/components/Notifications/NotificationLine.vue';
</script>

<template>
  <HDropdown
    class='notification-modal'
    position='bottom-right'
  >
    <template #activator='{ activatorClick }'>
      <HBadge
        :content='`${ total }`'
        hide-on-empty-content
      >
        <template #default>
          <NavbarItem
            :is-mobile='isMobile'
            :item="{
              name     : 'notifications',
              path     : 'notifications',
              function : () => openNotifications(activatorClick),
              icon     : { name : 'bell' }
            }"
            only-icon
          />
        </template>
      </HBadge>
    </template>
    <template #default='{ closeDropdown }'>
      <HRow
        v-loading='loading'
        align-items='center'
        class='notification-modal__title'
        gap='14'
        justify-content='space-between'
      >
        <p class='text-blue-400 text-bold'>
          {{ $tc('notifications.unread', notifications.length, { number : notifications.length }) }}
        </p>
        <HButton
          :disabled='notifications.length === 0'
          plain
          size='small'
          @click='markAllAsRead'
        >
          <template #default>
            {{ $t('notifications.buttons.markAllAsRead') }}
          </template>
        </HButton>
      </HRow>
      <HDivider />
      <HCol
        v-if='notifications.length > 0'
        class='notification-modal__content'
        @click='closeDropdown'
      >
        <NotificationLine
          v-for='(notif, index) in notifications'
          :key='notif.id'
          :notification='notif'
          :show-divider='index !== notifications.length -1'
          @mark-as-read='markAsRead'
        />
      </HCol>
      <HRow
        v-else
        class='text-s text-grey-500 padding-14'
        justify-content='center'
      >
        {{ $t('notifications.noUnreadData') }}
      </HRow>

      <HDivider />
      <HRow
        class='padding-8'
        justify-content='center'
      >
        <HButton
          plain
          size='small'
          @click='goToNotifications(closeDropdown)'
        >
          <template #default>
            {{ $t('main.buttons.viewAll') }}
          </template>
        </HButton>
      </HRow>
    </template>
  </HDropdown>
</template>

<script>
import { mapStores }    from 'pinia';
import { useUserStore } from '@/store/user';
import Service          from '@/api/service';

export default {
  name   : 'NotificationsModal',
  inject : ['displayAlert'],

  props : {
    isMobile : {
      type    : Boolean,
      default : false
    }
  },

  emits : ['clickOnItem'],

  data() {

    return {
      loading       : true,
      notifications : [],
      total         : 0,
      interval      : undefined
    };
  },

  computed : {
    ...mapStores(useUserStore)
  },

  async created() {

    await this.initNotifications();
    this.interval = setInterval(async () => {

      await this.initNotifications();
    }, 60000);
  },

  unmounted() {

    clearInterval(this.interval);
  },

  methods : {
    async initNotifications() {

      try {

        const notifications = await Service.getNotificationsUnread(this.userStore.tenant);
        this.notifications = notifications.results;
        this.total = notifications.pagination.total;

        this.loading = false;
      } catch {

        this.loading = false;
      }
    },

    openNotifications(activatorClick) {

      if (this.isMobile) {

        this.$emit('clickOnItem');
        this.$router.push({ name : 'notifications' });
        return;
      }

      activatorClick();
    },

    goToNotifications(closeDropdown) {

      this.$router.push({ name : 'notifications' });
      closeDropdown();
    },

    async markAllAsRead() {

      try {

        await Service.readAllNotifications(this.userStore.tenant);

        this.displayAlert({
          type   : 'success',
          action : 'update',
          model  : this.$t('model.notification')
        });

        await this.initNotifications();
      } catch {

        this.displayAlert({
          type   : 'error',
          action : 'updateError',
          model  : this.$t('model.notification')
        });
      }
    },

    async markAsRead(id) {

      try {

        await Service.readNotification(this.userStore.tenant, id);

        this.displayAlert({
          type   : 'success',
          action : 'update',
          model  : this.$t('model.notification')
        });

        await this.initNotifications();
      } catch {

        this.displayAlert({
          type   : 'error',
          action : 'updateError',
          model  : this.$t('model.notification')
        });
      }
    }
  }
};
</script>

<style rel='stylesheet/scss' lang='scss'>
.navbar--mobile .notification-modal .c-badge__container .c-badge {
  transform: translate(0, -50%);
  top: 50%;
}

.notification-modal .c-badge__container .c-badge {
  transform: translate(10%, -10%);
}
</style>

<style rel='stylesheet/scss' lang='scss' scoped>
.notification-modal__title {
  padding: 8px 14px;
}

.c-button {
  height: fit-content;
}
</style>